import { defineModule, } from 'direct-vuex';
import { isObject, } from '~/utils/models/isObject';

export interface ContentTileLink {
	target: string;
	href: string;
	text: string;
}

export interface ContentTileImage {
	url: string;
	altText: string;
}

export interface ContentTile {
	title: string;
	body: string;
	location: ContentTileLink;
	image: ContentTileImage;
}

export interface LoginTile {
	title: string;
	icon: string;
	description: string;
}

export interface RegistrationTileImage {
	altText: string;
	url: string;
}

export interface RegistrationTile {
	image: RegistrationTileImage;
}

export interface LoginPromo {
	icon: string;
	backgroundColour: string;
	title: string;
	description: string;
}

export interface LoginPromoBanner {
	loginPromo: LoginPromo | undefined | null;
}

export interface DashboardPopUpLink {
	target: string;
	href: string;
	text: string;
}

export interface DashboardPopUpImage {
	url: string;
	altText: string;
}

export interface DashboardPopupItems {
	title: string;
	body: string;
	location: DashboardPopUpLink;
	image: DashboardPopUpImage;
}

interface DashboardPopupDateRange {
	FromDate: Date;
	ToDate: Date;
}

interface DashboardNotificationsLocation {
	target: string;
	text: string;
	href: string;
}

interface DashboardNotificationListImage {
	url: string;
	altText: string;
}

interface DashboardNotificationList {
	title: string;
	description: string;
	image: DashboardNotificationListImage;
	location: DashboardNotificationsLocation;
	iconBackgroundColour: string;
	id: number;
}

export interface DashboardNotificationService {
	heading: string;
	isVisible: boolean;
	isAccordionExpanded: boolean;
	notificationList: DashboardNotificationList[];
}

export interface CustomContentState {
	dashboardContentTiles: readonly ContentTile[];
	loginContentTiles: readonly LoginTile[];
	registrationContent: RegistrationTile;
	loginPromoBanner?: LoginPromoBanner | undefined;
	dashboardPopupItems: readonly DashboardPopupItems[];
	dashboardPopupDateRange: DashboardPopupDateRange;
	dashboardNotificationService: DashboardNotificationService;
}

function isContentTileLink(candidate: unknown): candidate is ContentTileLink {
	const asContent = candidate as ContentTileLink;
	if (!isObject(candidate)) {
		return false;
	}

	return true &&
		typeof asContent.href === 'string' &&
		typeof asContent.target === 'string' &&
		typeof asContent.text === 'string' &&
		true;
}

function isContentTileImage(candidate: unknown): candidate is ContentTileImage {
	const asContent = candidate as ContentTileImage;
	if (!isObject(candidate)) {
		return false;
	}

	return true &&
		typeof asContent.url === 'string' &&
		typeof asContent.altText === 'string' &&
		true;
}

function isContentTile(candidate: unknown): candidate is ContentTile {
	const asContent = candidate as ContentTile;
	if (!isObject(candidate)) {
		return false;
	}

	return true &&
		typeof asContent.body === 'string' &&
		typeof asContent.title === 'string' &&
		isContentTileLink(asContent.location) &&
		isContentTileImage(asContent.image) &&
		true;
}

function isDashboardPopUpTileLink(candidate: unknown): candidate is DashboardPopUpLink {
	const asContent = candidate as DashboardPopUpLink;
	if (!isObject(candidate)) {
		return false;
	}

	return true &&
		typeof asContent.href === 'string' &&
		typeof asContent.target === 'string' &&
		typeof asContent.text === 'string' &&
		true;
}

function isDashboardPopUpTileImage(candidate: unknown): candidate is DashboardPopUpImage {
	const asContent = candidate as DashboardPopUpImage;
	if (!isObject(candidate)) {
		return false;
	}

	return true &&
		typeof asContent.url === 'string' &&
		typeof asContent.altText === 'string' &&
		true;
}

function isDashboardPopupItems(candidate: unknown): candidate is DashboardPopupItems {
	const asContent = candidate as DashboardPopupItems;
	if (!isObject(candidate)) {
		return false;
	}

	return true &&
		typeof asContent.body === 'string' &&
		typeof asContent.title === 'string' &&
		isDashboardPopUpTileLink(asContent.location) &&
		isDashboardPopUpTileImage(asContent.image) &&
		true;
}

function isDashboardNotificationService(candidate: unknown): candidate is DashboardNotificationService {
	const asContent = candidate as DashboardNotificationService;
	if (!isObject(candidate)) {
		return false;
	}
	return true &&
	typeof asContent.heading === 'string' &&
	typeof asContent.isAccordionExpanded === 'boolean' &&
	typeof asContent.isVisible === 'boolean' &&
	Array.isArray(asContent.notificationList) && asContent.notificationList.every((x) => isDashboardNotificationList(x)) &&
	true;
}

function isDashboardNotificationList(candidate: unknown): candidate is DashboardNotificationList {
	const asContent = candidate as DashboardNotificationList;
	if (!isObject(candidate)) {
		return false;
	}

	return true &&
		typeof asContent.title === 'string' &&
		typeof asContent.id === 'number' &&
		typeof asContent.description === 'string' &&
		typeof asContent.iconBackgroundColour === 'string' &&
		isDashboardNotificationListImage(asContent.image) &&
		isDashboardNotificationsLocation(asContent.location) &&
		true;
}

function isDashboardNotificationListImage(candidate: unknown): candidate is DashboardNotificationListImage {
	const asContent = candidate as DashboardNotificationListImage;
	if (!isObject(candidate)) {
		return true;
	}

	return true &&
		typeof asContent.url === 'string' &&
		typeof asContent.altText === 'string' &&
		true;
}

function isDashboardNotificationsLocation(candidate: unknown): candidate is DashboardNotificationsLocation {
	const asContent = candidate as DashboardNotificationsLocation;
	if (!isObject(candidate)) {
		return false;
	}

	return true &&
		typeof asContent.href === 'string' &&
		typeof asContent.target === 'string' &&
		typeof asContent.text === 'string' &&
		true;
}

function isLoginTile(candidate: unknown): candidate is LoginTile {
	const asContent = candidate as LoginTile;
	if (!isObject(candidate)) {
		return false;
	}

	return true &&
		typeof asContent.icon === 'string' &&
		typeof asContent.title === 'string' &&
		typeof asContent.description === 'string' &&
		true;
}

export function isLoginPromo(candidate: unknown): candidate is LoginPromo {
	const asContent = candidate as LoginPromo;
	if (!isObject(candidate)) {
		return false;
	}

	return true &&
		typeof asContent.icon === 'string' &&
		typeof asContent.backgroundColour === 'string' &&
		typeof asContent.title === 'string' &&
		typeof asContent.description === 'string' &&
		true;
}

function isLoginPromoBanner(candidate: unknown): candidate is LoginPromoBanner {
	const asContent = candidate as LoginPromoBanner;
	if (!isObject(candidate)) {
		return false;
	}

	return true &&
		(!asContent.loginPromo || isLoginPromo(asContent.loginPromo)) &&
		true;
}

function isDashboardPopupDateRange(candidate: unknown): candidate is DashboardPopupDateRange {
	const asContent = candidate as DashboardPopupDateRange;
	if (!isObject(candidate)) {
		return false;
	}

	return true &&
	typeof asContent.FromDate === 'string' &&
	typeof asContent.ToDate === 'string' &&
	true;
}

function isCustomContent(candidate: unknown): candidate is CustomContentState {
	const asContent = candidate as CustomContentState;
	if (!isObject(candidate)) {
		return false;
	}

	return true &&
		Array.isArray(asContent.dashboardContentTiles) && asContent.dashboardContentTiles.every((x) => isContentTile(x)) &&
		Array.isArray(asContent.loginContentTiles) && asContent.loginContentTiles.every((x) => isLoginTile(x)) &&
		(asContent.loginPromoBanner === undefined || isLoginPromoBanner(asContent.loginPromoBanner)) &&
		Array.isArray(asContent.dashboardPopupItems) && asContent.dashboardPopupItems.every((x) => isDashboardPopupItems(x)) &&
		(asContent.dashboardPopupDateRange === undefined || isDashboardPopupDateRange(asContent.dashboardPopupDateRange)) &&
		(asContent.dashboardNotificationService === undefined || isDashboardNotificationService(asContent.dashboardNotificationService)) &&
		true;
}

export default defineModule({
	namespaced: true as const,
	state: {} as CustomContentState,
	mutations: {
		hydrateCustomConfig(state, hydratedState: CustomContentState) {
			Object.assign(state, hydratedState);
		},
	},
});

export function validateCustomContent(customContentCandidate: unknown) : asserts customContentCandidate is CustomContentState {
	if (!isCustomContent(customContentCandidate)) {
		throw new Error('Candidate for custom content not valid');
	}
}
